/**
 * Ticimax E-commerce Platform Tracker
 * Auto-detects and tracks e-commerce events from Ticimax stores
 */
import BaseEcommerceTracker from './base';

export default class TicimaxTracker extends BaseEcommerceTracker {
  constructor(b2mTracker, platformName) {
    super(b2mTracker, platformName);
    
    // Ticimax-specific selectors
    this.selectors = {
      // Product page
      productDetail: '.urun-detay, .product-detail, [data-page="product"]',
      productName: '.urun-adi, .product-name, .product-title, h1.title',
      productPrice: '.fiyat, .price, .product-price, .satis-fiyati',
      productOldPrice: '.eski-fiyat, .old-price, .list-price',
      productImage: '.urun-resim img, .product-image img',
      productSku: '[data-sku], [data-product-sku], .stok-kodu',
      productBrand: '.marka, .brand, [data-brand]',
      productCategory: '.kategori, .breadcrumb, [data-category]',
      
      // Add to cart
      addToCartButton: '.sepete-ekle, .add-to-cart, [data-action="add-to-cart"], .btn-sepet',
      quantityInput: '.adet, .quantity, input[name="quantity"]',
      
      // Cart page
      cartPage: '.sepet-sayfa, .cart-page, [data-page="cart"]',
      cartItems: '.sepet-urun, .cart-item, .cart-product',
      cartTotal: '.toplam-fiyat, .cart-total, .grand-total',
      cartItemRemove: '.sil, .remove, .delete-item',
      
      // Checkout
      checkoutPage: '.odeme-sayfa, .checkout-page, [data-page="checkout"]',
      shippingForm: '.kargo-form, .shipping-form',
      paymentForm: '.odeme-form, .payment-form',
      
      // Order confirmation
      orderConfirmation: '.siparis-onay, .order-confirmation, [data-page="order-success"]',
      orderId: '.siparis-no, .order-id, .order-number',
      orderTotal: '.siparis-toplam, .order-total',
      
      // Category/List pages
      categoryPage: '.kategori-sayfa, .category-page, [data-page="category"]',
      productList: '.urun-listesi, .product-list, .products-grid',
      productCard: '.urun-kart, .product-card, .product-item',
      
      // Search
      searchInput: '.arama-input, .search-input, input[name="q"], input[name="search"]',
      searchResults: '.arama-sonuclari, .search-results',
      
      // Wishlist
      wishlistButton: '.favorilere-ekle, .add-to-wishlist, .wishlist-btn',
      
      // Promotions
      banner: '.banner, .slider-item, .kampanya',
      couponInput: '.kupon-input, .coupon-input, input[name="coupon"]',
      couponApply: '.kupon-uygula, .apply-coupon'
    };
    
    // Ticimax-specific URL patterns
    this.urlPatterns = {
      product: ['/urun/', '/product/', '/p/', '/-p-'],
      category: ['/kategori/', '/category/', '/c/', '/-c-'],
      cart: ['/sepet', '/cart', '/basket'],
      checkout: ['/odeme', '/checkout', '/siparis'],
      orderConfirmation: ['/siparis-onay', '/order-confirmation', '/tesekkurler'],
      search: ['/arama', '/search', '?q=', '?search='],
      wishlist: ['/favoriler', '/wishlist', '/begeniler']
    };
  }

  /**
   * Initialize Ticimax-specific tracking
   */
  init() {
    super.init();
    
    // Setup Ticimax-specific event listeners
    this.setupAddToCartTracking();
    this.setupRemoveFromCartTracking();
    this.setupWishlistTracking();
    this.setupSearchTracking();
    this.setupCouponTracking();
    this.setupPromotionTracking();
    
    // Listen for Ticimax-specific custom events
    this.setupTicimaxEventListeners();
    
    console.log('[B2M Ticimax] ✅ Ticimax-specific tracking initialized');
  }

  /**
   * Get page type from URL - Ticimax specific
   */
  getPageType(path) {
    const url = path.toLowerCase();
    
    for (const [type, patterns] of Object.entries(this.urlPatterns)) {
      for (const pattern of patterns) {
        if (url.includes(pattern)) {
          return type;
        }
      }
    }
    
    return 'other';
  }

  /**
   * Auto-track product page
   */
  autoTrackProductPage() {
    // Wait for DOM to be ready
    this.waitForElement(this.selectors.productDetail, (productContainer) => {
      const product = this.extractTicimaxProduct(productContainer);
      
      if (product.item_id || product.item_name) {
        this.trackViewItem([product], {
          page_type: 'product_detail',
          source: 'auto_detect'
        });
      }
    });
  }

  /**
   * Auto-track category page
   */
  autoTrackCategoryPage() {
    this.waitForElement(this.selectors.productList, (productList) => {
      const items = this.extractProductList(productList);
      const categoryName = this.extractCategoryName();
      
      if (items.length > 0) {
        this.trackViewItemList(items, categoryName, {
          page_type: 'category',
          source: 'auto_detect'
        });
      }
      
      // Setup click tracking on product cards
      this.setupProductCardClicks(productList);
    });
  }

  /**
   * Auto-track cart page
   */
  autoTrackCartPage() {
    this.waitForElement(this.selectors.cartPage, (cartContainer) => {
      const items = this.extractCartItems(cartContainer);
      const total = this.extractCartTotal(cartContainer);
      
      if (items.length > 0) {
        this.trackViewCart(items, total, 'TRY', {
          page_type: 'cart',
          source: 'auto_detect'
        });
      }
    });
  }

  /**
   * Auto-track checkout page
   */
  autoTrackCheckoutPage() {
    this.waitForElement(this.selectors.checkoutPage, (checkoutContainer) => {
      const items = this.extractCartItems(checkoutContainer);
      const total = this.extractCartTotal(checkoutContainer);
      
      this.trackBeginCheckout(items, total, 'TRY', {
        page_type: 'checkout',
        source: 'auto_detect'
      });
      
      // Track shipping info when filled
      this.setupShippingInfoTracking(checkoutContainer);
      
      // Track payment info when selected
      this.setupPaymentInfoTracking(checkoutContainer);
    });
  }

  /**
   * Auto-track order confirmation page
   */
  autoTrackOrderConfirmation() {
    this.waitForElement(this.selectors.orderConfirmation, (confirmContainer) => {
      const orderData = this.extractOrderData(confirmContainer);
      
      if (orderData.transaction_id) {
        this.trackPurchase(orderData, {
          page_type: 'order_confirmation',
          source: 'auto_detect'
        });
      }
    });
  }

  /**
   * Auto-track search page
   */
  autoTrackSearchPage() {
    const searchTerm = this.getSearchTermFromURL();
    
    if (searchTerm) {
      this.waitForElement(this.selectors.searchResults, (resultsContainer) => {
        const items = this.extractProductList(resultsContainer);
        
        this.trackSearch(searchTerm, {
          results_count: items.length,
          page_type: 'search',
          source: 'auto_detect'
        });
        
        if (items.length > 0) {
          this.trackViewItemList(items, `Search: ${searchTerm}`, {
            search_term: searchTerm
          });
        }
      });
    }
  }

  // ============================================
  // Ticimax-Specific Event Tracking Setup
  // ============================================

  /**
   * Setup add to cart button tracking
   */
  setupAddToCartTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.addToCartButton);
      if (!button) return;
      
      // Find product container
      const productContainer = button.closest(this.selectors.productDetail) || 
                               button.closest(this.selectors.productCard) ||
                               document;
      
      const product = this.extractTicimaxProduct(productContainer);
      const quantity = this.getQuantityValue(productContainer);
      
      if (product.item_id || product.item_name) {
        product.quantity = quantity;
        this.trackAddToCart([product], product.price * quantity, 'TRY', {
          source: 'button_click',
          button_text: button.textContent.trim()
        });
      }
    });
  }

  /**
   * Setup remove from cart tracking
   */
  setupRemoveFromCartTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.cartItemRemove);
      if (!button) return;
      
      const cartItem = button.closest(this.selectors.cartItems);
      if (!cartItem) return;
      
      const product = this.extractCartItemProduct(cartItem);
      
      if (product.item_id || product.item_name) {
        this.trackRemoveFromCart([product], {
          source: 'button_click'
        });
      }
    });
  }

  /**
   * Setup wishlist tracking
   */
  setupWishlistTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.wishlistButton);
      if (!button) return;
      
      const productContainer = button.closest(this.selectors.productDetail) ||
                               button.closest(this.selectors.productCard) ||
                               document;
      
      const product = this.extractTicimaxProduct(productContainer);
      
      if (product.item_id || product.item_name) {
        this.trackAddToWishlist([product], {
          source: 'button_click'
        });
      }
    });
  }

  /**
   * Setup search tracking
   */
  setupSearchTracking() {
    const searchInput = document.querySelector(this.selectors.searchInput);
    if (!searchInput) return;
    
    const searchForm = searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        const searchTerm = searchInput.value.trim();
        if (searchTerm) {
          this.trackSearch(searchTerm, {
            source: 'form_submit'
          });
        }
      });
    }
  }

  /**
   * Setup coupon tracking
   */
  setupCouponTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.couponApply);
      if (!button) return;
      
      const couponInput = document.querySelector(this.selectors.couponInput);
      const couponCode = couponInput?.value?.trim();
      
      if (couponCode) {
        this.b2mTracker.track('coupon_apply', {
          platform: this.platformName,
          coupon_code: couponCode,
          source: 'button_click'
        });
      }
    });
  }

  /**
   * Setup promotion/banner tracking
   */
  setupPromotionTracking() {
    // Track visible promotions
    const banners = document.querySelectorAll(this.selectors.banner);
    banners.forEach((banner, index) => {
      this.trackPromotionImpression(banner, index);
      
      // Track clicks
      banner.addEventListener('click', () => {
        this.trackPromotionClick(banner, index);
      });
    });
  }

  /**
   * Setup product card click tracking
   */
  setupProductCardClicks(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    
    productCards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        // Don't track if clicking on add to cart or wishlist buttons
        if (e.target.closest(this.selectors.addToCartButton) ||
            e.target.closest(this.selectors.wishlistButton)) {
          return;
        }
        
        const product = this.extractTicimaxProduct(card);
        product.index = index;
        
        if (product.item_id || product.item_name) {
          this.trackSelectItem([product], {
            source: 'product_card_click'
          });
        }
      });
    });
  }

  /**
   * Listen for Ticimax-specific custom events
   */
  setupTicimaxEventListeners() {
    // Ticimax may fire custom events
    window.addEventListener('ticimax:addToCart', (e) => {
      if (e.detail?.product) {
        this.trackAddToCart([this.normalizeTicimaxProduct(e.detail.product)]);
      }
    });
    
    window.addEventListener('ticimax:purchase', (e) => {
      if (e.detail?.order) {
        this.trackPurchase(this.normalizeTicimaxOrder(e.detail.order));
      }
    });
    
    // Also listen on document
    document.addEventListener('addToCart', (e) => {
      if (e.detail) {
        this.trackAddToCart([this.normalizeTicimaxProduct(e.detail)]);
      }
    });
  }

  /**
   * Setup shipping info tracking on checkout
   */
  setupShippingInfoTracking(container) {
    const shippingOptions = container.querySelectorAll('input[name*="shipping"], input[name*="kargo"]');
    
    shippingOptions.forEach(option => {
      option.addEventListener('change', (e) => {
        const label = e.target.closest('label')?.textContent || 
                      document.querySelector(`label[for="${e.target.id}"]`)?.textContent ||
                      e.target.value;
        
        this.trackAddShippingInfo({
          shipping_tier: label.trim(),
          value: this.extractCartTotal(container),
          currency: 'TRY'
        });
      });
    });
  }

  /**
   * Setup payment info tracking on checkout
   */
  setupPaymentInfoTracking(container) {
    const paymentOptions = container.querySelectorAll('input[name*="payment"], input[name*="odeme"]');
    
    paymentOptions.forEach(option => {
      option.addEventListener('change', (e) => {
        const label = e.target.closest('label')?.textContent ||
                      document.querySelector(`label[for="${e.target.id}"]`)?.textContent ||
                      e.target.value;
        
        this.trackAddPaymentInfo({
          payment_type: label.trim(),
          value: this.extractCartTotal(container),
          currency: 'TRY'
        });
      });
    });
  }

  // ============================================
  // Data Extraction Methods
  // ============================================

  /**
   * Extract product data from Ticimax product container
   */
  extractTicimaxProduct(container) {
    const product = {
      item_id: null,
      item_name: null,
      price: 0,
      item_brand: null,
      item_category: null,
      item_variant: null,
      currency: 'TRY'
    };
    
    // Try data attributes first
    product.item_id = container.dataset?.productId || 
                      container.dataset?.urunId ||
                      container.dataset?.id ||
                      container.querySelector('[data-product-id]')?.dataset?.productId ||
                      container.querySelector('[data-urun-id]')?.dataset?.urunId;
    
    // Get name
    const nameEl = container.querySelector(this.selectors.productName);
    product.item_name = nameEl?.textContent?.trim() || container.dataset?.productName;
    
    // Get price
    const priceEl = container.querySelector(this.selectors.productPrice);
    if (priceEl) {
      product.price = this.parsePrice(priceEl.textContent);
    }
    
    // Get brand
    const brandEl = container.querySelector(this.selectors.productBrand);
    product.item_brand = brandEl?.textContent?.trim() || container.dataset?.brand;
    
    // Get category from breadcrumb or data attribute
    product.item_category = this.extractCategoryName() || container.dataset?.category;
    
    // Get SKU
    const skuEl = container.querySelector(this.selectors.productSku);
    if (skuEl) {
      product.item_id = product.item_id || skuEl.textContent.trim();
    }
    
    // Try to get from Schema.org markup
    const schemaProduct = container.querySelector('[itemtype*="schema.org/Product"]');
    if (schemaProduct) {
      product.item_name = product.item_name || schemaProduct.querySelector('[itemprop="name"]')?.textContent?.trim();
      product.item_brand = product.item_brand || schemaProduct.querySelector('[itemprop="brand"]')?.textContent?.trim();
      const schemaPriceEl = schemaProduct.querySelector('[itemprop="price"]');
      if (schemaPriceEl && !product.price) {
        product.price = this.parsePrice(schemaPriceEl.content || schemaPriceEl.textContent);
      }
    }
    
    // Try window variables (Ticimax often exposes product data)
    if (window.productData) {
      product.item_id = product.item_id || window.productData.id;
      product.item_name = product.item_name || window.productData.name;
      product.price = product.price || window.productData.price;
      product.item_brand = product.item_brand || window.productData.brand;
    }
    
    return product;
  }

  /**
   * Extract products from list/grid
   */
  extractProductList(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    const items = [];
    
    productCards.forEach((card, index) => {
      const product = this.extractTicimaxProduct(card);
      product.index = index;
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract cart items
   */
  extractCartItems(container) {
    const cartItems = container.querySelectorAll(this.selectors.cartItems);
    const items = [];
    
    cartItems.forEach((item, index) => {
      const product = this.extractCartItemProduct(item);
      product.index = index;
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract product from cart item row
   */
  extractCartItemProduct(cartItem) {
    const product = this.extractTicimaxProduct(cartItem);
    
    // Get quantity from cart item
    const quantityEl = cartItem.querySelector(this.selectors.quantityInput);
    product.quantity = parseInt(quantityEl?.value) || 1;
    
    return product;
  }

  /**
   * Extract cart total
   */
  extractCartTotal(container) {
    const totalEl = container.querySelector(this.selectors.cartTotal);
    if (totalEl) {
      return this.parsePrice(totalEl.textContent);
    }
    return 0;
  }

  /**
   * Extract order data from confirmation page
   */
  extractOrderData(container) {
    const orderData = {
      transaction_id: null,
      value: 0,
      currency: 'TRY',
      items: [],
      tax: 0,
      shipping: 0
    };
    
    // Get order ID
    const orderIdEl = container.querySelector(this.selectors.orderId);
    orderData.transaction_id = orderIdEl?.textContent?.trim()?.replace(/[^\d]/g, '') ||
                               container.dataset?.orderId;
    
    // Get total
    const totalEl = container.querySelector(this.selectors.orderTotal);
    if (totalEl) {
      orderData.value = this.parsePrice(totalEl.textContent);
    }
    
    // Get items if available
    orderData.items = this.extractCartItems(container);
    
    // Try window variables
    if (window.orderData) {
      orderData.transaction_id = orderData.transaction_id || window.orderData.id;
      orderData.value = orderData.value || window.orderData.total;
      orderData.tax = window.orderData.tax || 0;
      orderData.shipping = window.orderData.shipping || 0;
    }
    
    return orderData;
  }

  /**
   * Extract category name from breadcrumb
   */
  extractCategoryName() {
    const breadcrumb = document.querySelector('.breadcrumb, .kategori-yol, [class*="breadcrumb"]');
    if (breadcrumb) {
      const items = breadcrumb.querySelectorAll('a, span');
      const categories = Array.from(items).map(item => item.textContent.trim()).filter(Boolean);
      // Remove "Ana Sayfa" / "Home" and join
      return categories.filter(c => !['ana sayfa', 'home', 'anasayfa'].includes(c.toLowerCase())).join(' > ');
    }
    return null;
  }

  /**
   * Get search term from URL
   */
  getSearchTermFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('q') || urlParams.get('search') || urlParams.get('arama') || urlParams.get('kelime');
  }

  /**
   * Get quantity value from container
   */
  getQuantityValue(container) {
    const quantityEl = container.querySelector(this.selectors.quantityInput);
    return parseInt(quantityEl?.value) || 1;
  }

  /**
   * Normalize Ticimax product object to standard format
   */
  normalizeTicimaxProduct(product) {
    return {
      item_id: product.id || product.urunId || product.productId,
      item_name: product.name || product.urunAdi || product.productName,
      price: parseFloat(product.price || product.fiyat) || 0,
      item_brand: product.brand || product.marka,
      item_category: product.category || product.kategori,
      quantity: parseInt(product.quantity || product.adet) || 1,
      currency: product.currency || 'TRY'
    };
  }

  /**
   * Normalize Ticimax order object to standard format
   */
  normalizeTicimaxOrder(order) {
    return {
      transaction_id: order.id || order.siparisId || order.orderId,
      value: parseFloat(order.total || order.toplam) || 0,
      currency: order.currency || 'TRY',
      tax: parseFloat(order.tax || order.kdv) || 0,
      shipping: parseFloat(order.shipping || order.kargo) || 0,
      items: (order.items || order.urunler || []).map(item => this.normalizeTicimaxProduct(item))
    };
  }

  /**
   * Track promotion impression
   */
  trackPromotionImpression(banner, index) {
    const promotionData = {
      promotion_id: banner.dataset?.promotionId || banner.dataset?.campaignId || `promo_${index}`,
      promotion_name: banner.dataset?.promotionName || banner.querySelector('img')?.alt || `Promotion ${index + 1}`,
      creative_slot: `slot_${index}`,
      location_id: 'homepage_banner'
    };
    
    this.b2mTracker.track('view_promotion', {
      platform: this.platformName,
      ...promotionData
    });
  }

  /**
   * Track promotion click
   */
  trackPromotionClick(banner, index) {
    const promotionData = {
      promotion_id: banner.dataset?.promotionId || banner.dataset?.campaignId || `promo_${index}`,
      promotion_name: banner.dataset?.promotionName || banner.querySelector('img')?.alt || `Promotion ${index + 1}`,
      creative_slot: `slot_${index}`
    };
    
    this.b2mTracker.track('select_promotion', {
      platform: this.platformName,
      ...promotionData
    });
  }

  /**
   * Wait for element to appear in DOM
   */
  waitForElement(selector, callback, timeout = 5000) {
    const element = document.querySelector(selector);
    
    if (element) {
      callback(element);
      return;
    }
    
    const observer = new MutationObserver((mutations, obs) => {
      const el = document.querySelector(selector);
      if (el) {
        obs.disconnect();
        callback(el);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Timeout fallback
    setTimeout(() => {
      observer.disconnect();
    }, timeout);
  }
}
